import { cx } from '@linaria/core';
import { mdiInformationOutline, mdiLogin, mdiLogout, mdiMessageAlertOutline } from '@mdi/js';
import MdiIcon from '@mdi/react';
import { FunctionComponent, HTMLAttributes, memo, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import type { FloaterModal, PMChatsState } from '../../../../types/state';
import { openMessage } from '../../../actions/message';
import { goToMessages, goToModelsYouMayLike, goToRooms } from '../../../actions/nav';
import { AppStateType, GatedDisplayModes, ListType, SecureStateType } from '../../../constants/enums';
import { QUICK_REPLY } from '../../../constants/floaters';
import { BROADCASTING_STATE } from '../../../constants/myWebcam';
import { locales } from '../../../locales/en';
import { logout } from '../../../mfcLogic/auth';
import { getLens } from '../../../mfcLogic/lens';
import { getShareFeedUrl } from '../../../mfcLogic/mfcShare';
import { isLevelPremium } from '../../../mfcLogic/userProperties/level';
import { APPSTATE, TransitionTypes } from '../../../state';
import { slugToID } from '../../../state/internal/users';
import { openAboutDrawer, openAccountDrawer, openAuthenticationDrawer, openContactSupportDrawer, openLoungesDrawer, openMyAccountDrawer, openSignupDrawer, openUserFeedbackDrawer } from '../../../state/modals/state.drawer';
import { minimizeFloater } from '../../../state/modals/state.floater';
import { openRoom } from '../../../state/state.rooms';
import { capitalize } from '../../../utilities/string';
import { motionTransition } from '../../../utilities/transitions';
import ButtonDown from '../../Buttons/ButtonDown';
import ButtonUp from '../../Buttons/ButtonUp';
import ButtonUserBroadcast from '../../Buttons/ButtonUserBroadcast';
import IconButton, { IconButtonProps } from '../../Buttons/IconButton';
import BadgeAvatar from '../../common/BadgeAvatar';
import { BadgedIcon } from '../../common/CountBadge';
import { useAppStateSelector } from '../../Hooks/useAppState';
import { useLayoutSelector } from '../../Hooks/useLayout';
import { useList } from '../../Hooks/useList';
import { useMailStateSelector } from '../../Hooks/useMail';
import { useDialogSelector, useDrawerSelector, useFloaters } from '../../Hooks/useModal';
import { usePMSelector } from '../../Hooks/usePM';
import { usePortalStateSelector } from '../../Hooks/usePortalState';
import { useRoomsCount } from '../../Hooks/useRooms';
import { useRouteSelector } from '../../Hooks/useRoute';
import { useScreenStateSelector } from '../../Hooks/useScreenState';
import { useSecureState } from '../../Hooks/useSecureState';
import { useUserSelector } from '../../Hooks/useUser';
import { IconProps } from '../../Icons';
import AccountCircleOutlined from '../../Icons/AccountCircleOutlined';
import Archives from '../../Icons/Archives';
import Binoculars from '../../Icons/Binoculars';
import ContactSupport from '../../Icons/ContactSupport';
import CrownOutline from '../../Icons/CrownOutline';
import Explore from '../../Icons/Explore';
import Friend from '../../Icons/Friend';
import GlassCocktail from '../../Icons/GlassCocktail';
import Home from '../../Icons/Home';
import Mail from '../../Icons/Mail';
import Message from '../../Icons/Message';
import MfcShareFeed from '../../Icons/MfcShareFeed';
import More from '../../Icons/More';
import Newspaper from '../../Icons/Newspaper';
import Note from '../../Icons/Note';
import PrivateGallery from '../../Icons/PrivateGallery';
import Recommendation from '../../Icons/Recommendation';
import Room from '../../Icons/Room';
import APP_BREAKPOINTS from '../../styles/breakpoints';
import { palette } from '../../styles/colors';
import { COMPONENT_HEIGHTS, COMPONENT_WIDTHS, PAPER_HEIGHTS } from '../../theme';
import Link from '../Link';
import "./Nav.linaria.module.css!=!./Nav?./Nav.linaria.module.css?css=H4sIAAAAAAAAA7VW246kKhR976+o89CZnmToiKVV6nwMQUFEERRQyzLz7yd2K16mZ5LzcOrByoIF7r3WZuM7tjXCGsG0CVSVvR/hlAv6AIRrmlmuZJIp0dXy56+XD15KEWzjLGPPE/xcNmjcJFLNf+sK6iOoTKv8fiooZ4VNoOe9rrOMo0dZBSSeaqwZl4l3wZ1V67SACA4dMzI8wUn1VOdCDWBM9gskQ23Y5WqcTKaVECClBe650omplbLFysMa9brP6OOIzslrNawr8gzBMQ2kaE5wKjtjeT6CTElLpU1MgzMKUmoHSp1w/ehkGDixxUGFyiAY9u0waJdXUnBCtuWxQdA+Bt6LiXDTCDwmc6jrdF2hwq/r8jY1yvDP0KnAlvd0pRQIFjdVds8jmrDgTAJuaW2SjEpL9brCJwhWHHfweYJTirOKadVJAjIllE56rN8A2Ia/r3sMLSKlfw/j6Qm4JPSRxOvvH143SlssrXNboNwyLvwjmhpMCJcM6I/yiRqX9l0gGBkR22baxyFxD9LOWiVdHGGJOGlyFkyp0oRqoDHhnUm8lXDNj4RtJtcoKFkUh5Og1lINZn+5ZIlUusZi56G8KxKVm4WdNNQl1/frNkPBLf3YhZ72gFjXcYURNCblpLiYBstEYGOByoEdG3qKPtxKCI7wXgwVgneW3XDz1VhSzJH9yTyczdUCPjjfd3UFo64PRbsVFpfc8i1m9lyEO6LlPANBc7tpmUVIE1V20RFNuZIWGP6kSby5mxmU5pKp/Ig+ycNnLwk9tze7IhinvWrUCU6CSwpc89leYFoEhW846SdLHxY446gQvDHc7GQwVg5RtKmAU6NEZ93xshWCbVnZsJisapLrfZdHhGB/e96gPcFd2js6u+5PgUOHLHZngD0RtBkrQ3aCBwf84BAPEVX06I9oFw0MDi5IfhceO6KDC7eDC7mfmVEf0dGD9zsM/CjcnvsDfTWKUHI40HNn1fMxmG1yxVleEaQPaHw+1fgBPttq6G+hC4XgGARK5CfoOsps1S7XecfgluY62u0I9yUzt/1dG93gb7fAsZcOLRqfDY+064O+53u74llOzRyQGw6fNvav672R4NxS/cXtAVvd8c5HrLL5ylqU9nehY23HKEZMjENeL7RG8TlKQHsqrUmkkq6g4Z1SiX0ExzEn2X1ZsGb37Zu7cZ8k1QzdbxEpioW1tZhk9h1rwOaORaV9e7nsflZdUmWtqn8chnddfLtRLr73eqRpluI3Pwx/XLaH9/0Sea+O5mrlxrf2Pr9wExn6Zc/S0Kmc0lxp+oXMOGxHFi0qL6wvZIaUD4SZVeeF+DehWVU8m8AJvaz4XWlYSY8WFNWiqBq+8v6D1lY1/7vQ81dC16or6U/wT/fO6e0u2WuqldYIjqKhobx83ueLLcFe76wuw46eiYsze+b8DdUJ+shT9w2VCpVVP3+9/AuF9efyEwsAAA=="
const navLeftStyles = "atm_ar_1bp4okc atm_be_1q9ccgz atm_e2_1osqo2v atm_gi_xjk4d9 atm_l1_1wugsn5 atm_ng_q5ufoy";
const navBottomStyles = "atm_ar_vrvcex atm_be_1q9ccgz atm_fc_1yb4nlp atm_vy_1osqo2v atm_ks_15vqwwr";
const navContainerStyles = "atm_9s_1txwivl";
const navInlineButtonStyles = "atm_mk_h2mmj6 atm_vy_1osqo2v atm_ks_15vqwwr";
const navRootStyles = "atm_mk_h2mmj6 atm_9s_1txwivl atm_ar_1bp4okc atm_be_1q9ccgz atm_h_1h6ojuz";
const bottonNavRootStyles = "atm_2d_1kiau1z";
const footerPaperHeightAdjustment = "atm_wq_dj2759";
const navButtonStyles = "atm_vy_1osqo2v atm_ll_ftgil2 atm_7l_18sl9tp atm_5j_idpfg4 atm_3f_idpfg4 atm_fr_4jg895 atm_ks_n7od8j atm_vv_4jg895 atm_1arm9ka_1ssbidh";
const stylesGenericNavButton = "atm_9s_1txwivl atm_1y17hwk_17gc6ap";
const rootButtonLabelStyles = "atm_mk_18uv5lq atm_gz_idpfg4 atm_c8_rdoju8 atm_cs_bfngof atm_g3_19bvopo atm_7l_18sl9tp atm_sq_1l2sidv atm_vv_4jg895";
const leftNavLabelStyles = "atm_mk_stnw88 atm_tk_1qjkt5h atm_c8_1v6z61t atm_g3_ftgil2";
const expandedLabelStyles = "atm_gz_1tcgj5g atm_c8_dlk8xv atm_cs_ni7l0g atm_g3_f2csyr";
const activeLabelStyles = "atm_7l_13soded";
const quickReplyLabelStyles = "atm_j3_1ex1s2i atm_lo_1y44olf";
const expandedQuickReplyStyles = "atm_j3_146bfr8";
const onMyAccount = () => {
  getLens()?.MyAccount_Click();
  openMyAccountDrawer();
};
const getButtonHeight = (leftNav: boolean) => {
  if (leftNav) return COMPONENT_HEIGHTS.navExpanded;
  return COMPONENT_HEIGHTS.navBottom - 1;
};
const FeedbackOutlinedIcon = ({
  color
}: IconProps) => <MdiIcon path={mdiMessageAlertOutline} size={1} color={color} />;
const InfoOutlinedIcon = ({
  color
}: IconProps) => <MdiIcon path={mdiInformationOutline} size={1} color={color} />;
const LoginIcon = ({
  color
}: IconProps) => <MdiIcon path={mdiLogin} size={1} color={color} />;
const LogoutIcon = ({
  color
}: IconProps) => <MdiIcon path={mdiLogout} size={1} color={color} />;
interface IStyledIconProps extends IconButtonProps {
  generic?: boolean;
  expanded: boolean;
  leftNav: boolean;
}
const StyledIconButton = (props: IStyledIconProps) => {
  const {
    expanded,
    generic,
    leftNav,
    children,
    ...directProps
  } = props;
  const styles = {
    flexDirection: generic || expanded ? 'row' : 'column',
    flex: 1,
    justifyContent: !generic && expanded ? 'flex-start' : 'center',
    height: leftNav ? getButtonHeight(leftNav) : `calc(var(--saib) + ${COMPONENT_HEIGHTS.navBottom - 1}px)`,
    paddingTop: !leftNav ? 0 : 8,
    paddingLeft: expanded ? 16 : 8,
    paddingBottom: !leftNav ? 'var(--saib)' : 8
  };
  if (generic) {
    return <div style={styles as any} className={cx(navButtonStyles, stylesGenericNavButton)}>
                {children}
            </div>;
  }
  return <IconButton style={styles as any} {...directProps} className={navButtonStyles}>
            {children}
        </IconButton>;
};
interface IStyledButtonTitle {
  active: boolean;
  expanded: boolean;
  quickReply?: boolean;
  leftNav: boolean;
  labelText?: string;
}
const StyledButtonTitle = (props: IStyledButtonTitle) => {
  const {
    active,
    expanded,
    quickReply,
    leftNav,
    labelText
  } = props;
  return <span className={cx(rootButtonLabelStyles, leftNav && !expanded && leftNavLabelStyles, expanded && expandedLabelStyles, active && activeLabelStyles, quickReply && quickReplyLabelStyles, quickReply && expanded && expandedQuickReplyStyles)}>
            {labelText}
        </span>;
};
interface NavButtonProps extends HTMLAttributes<HTMLDivElement> {
  leftNav: boolean;
  children?: ReactNode;
}
const StyledNavButtonContainer = (props: NavButtonProps) => {
  const {
    leftNav,
    children,
    ...divProps
  } = props;
  return <div {...divProps} style={{
    flex: leftNav ? 0 : 1,
    zIndex: 1
  }}>
            {children}
        </div>;
};
const upButtonContainerStyles = "atm_mk_stnw88 atm_9s_1txwivl atm_ar_1bp4okc atm_fc_1h6ojuz atm_h_1h6ojuz atm_vy_1osqo2v atm_wq_yzpi8r atm_tk_idpfg4 atm_5zt923_1osqo2v atm_1qruiu2_gktfv atm_arty89_glywfm atm_17eena2_1yyfdc7 atm_nzdbrg_768dhh";
const downButtonContainerStyles = "atm_mk_stnw88 atm_9s_1txwivl atm_ar_1bp4okc atm_fc_1h6ojuz atm_h_1h6ojuz atm_vy_1osqo2v atm_wq_yzpi8r atm_6i_idpfg4 atm_12jvgb5_1osqo2v atm_a5qyg8_gktfv atm_1eiwdgs_glywfm atm_1gkhzp4_1yyfdc7 atm_1kn0ehe_mlhkpi";
const arrowButtonWrapperStyles = "atm_9s_1txwivl atm_ar_1bp4okc atm_fc_1h6ojuz atm_h_1h6ojuz atm_vy_1osqo2v atm_2d_1uqo3dv atm_13brorr_1ylpe5n atm_1cmj5ue_1ylpe5n";
interface IButton {
  onClick(): void;
}
const DownButton = memo(({
  onClick
}: IButton) => <div className={downButtonContainerStyles}>
        <div className={arrowButtonWrapperStyles}>
            <ButtonDown onClick={onClick} />
        </div>
    </div>);
const UpButton = memo(({
  onClick
}: IButton) => <div className={upButtonContainerStyles}>
        <div className={arrowButtonWrapperStyles}>
            <ButtonUp onClick={onClick} />
        </div>
    </div>);
const linkStyles = "atm_9s_1ulexfb";
interface INavButton {
  active?: boolean;
  count?: number;
  disabled?: boolean;
  expanded?: boolean;
  filled?: boolean;
  Icon?: FunctionComponent<IconProps>;
  children?: any;
  label: string;
  leftNav: boolean;
  onClick?(e?: any): void;
  styles?: string;
  title?: string;
  tooltip?: string;
}
const NavButton = memo((props: INavButton) => {
  const {
    children,
    active,
    count,
    disabled,
    expanded,
    filled,
    Icon,
    label,
    leftNav,
    onClick,
    styles,
    title,
    tooltip
  } = props;
  /// this isnt even working so let's shelve it for the moment'
  // <StyledTooltip
  //     title={title ?? locales.nav.expanded[label]`)}
  //     disableHoverListener={true}
  //     placement="right"
  // >
  // </StyledTooltip>
  return <div title={tooltip} className={cx(navInlineButtonStyles, styles)} style={{
    height: leftNav ? getButtonHeight(leftNav) : `calc(var(--saib) + ${COMPONENT_HEIGHTS.navBottom - 1}px)`
  }}>
            <StyledIconButton onClick={!disabled ? onClick : undefined} data-testid={`NavButton__${capitalize(label)}`} expanded={expanded} leftNav={leftNav} disabled={disabled} generic={!!children}>
                {!!children && children}
                {!children && <BadgedIcon count={count} size="large">
                        <Icon color={disabled ? palette.disabledIcons : active ? palette.contrastIcons : 'currentColor'} {...filled || active ? {
          filled: true
        } : {}} />
                    </BadgedIcon>}
                <StyledButtonTitle active={active} expanded={expanded} leftNav={leftNav} labelText={title ?? locales.nav[expanded ? 'expanded' : 'compact'][label]} />
            </StyledIconButton>
        </div>;
});
const hiddenOverflow = "atm_ks_15vqwwr";
interface IEphemeralChat {
  expandedNav: boolean;
  leftNav: boolean;
  pm: FloaterModal;
  messages: PMChatsState;
  vertical: boolean;
}
const EphemeralChat = memo(({
  pm,
  expandedNav,
  leftNav,
  messages,
  vertical
}: IEphemeralChat) => {
  const {
    id,
    minimized
  } = pm;
  const name = useUserSelector(id, 'name');
  const offline = useUserSelector(id, 'isOffline');
  const unread = messages[id]?.unread ?? 0;
  const chatClick = useCallback(() => {
    // don't allow mobile landscape to pop open quick replies
    return !vertical && APPSTATE.app.isMobile ? openMessage(id, TransitionTypes.FLOATER) : motionTransition(() => minimizeFloater(id, QUICK_REPLY, !minimized), {
      id,
      transitionSource: minimized ? TransitionTypes.NAV : TransitionTypes.FLOATER,
      transitionDestination: minimized ? TransitionTypes.FLOATER : TransitionTypes.NAV
    });
  }, [id, minimized, vertical]);
  return <StyledNavButtonContainer leftNav={leftNav} onClick={chatClick}>
                <div className={hiddenOverflow}>
                    <StyledIconButton
      // onClick={() =>
      //     minimizeFloater(id, QUICK_REPLY, !minimized)
      // }
      data-testid={`NavButton__EphemeralChat`} expanded={expandedNav} leftNav={leftNav}>
                        <BadgedIcon count={unread} size="large">
                            <BadgeAvatar isOffline={offline} alt={name} size={24} id={id} disableDrag />
                        </BadgedIcon>
                        <StyledButtonTitle active={!minimized} expanded={expandedNav} leftNav={leftNav} quickReply labelText={name}></StyledButtonTitle>
                    </StyledIconButton>
                </div>
            </StyledNavButtonContainer>;
});
const EphemeralChats = memo((props: Omit<IEphemeralChat, 'pm' | 'vertical'>) => {
  const floaters = useFloaters();
  const vertical = useScreenStateSelector('portrait');
  const pmFloaters = floaters.filter(floater => floater.show && floater.type === QUICK_REPLY);
  const hasEphemeralChats = pmFloaters.length > 0;
  if (!hasEphemeralChats) return null;
  return <>
                {pmFloaters.map(pm => <EphemeralChat key={`${pm.id}-ephemeral-chat`} pm={pm} vertical={vertical} {...props} />)}
            </>;
});
interface INav {
  showBottomNav?: boolean;
  authenticated: boolean;
  isMobile: boolean;
  vertical: boolean;
}
const Nav = memo((props: INav) => {
  const {
    authenticated,
    showBottomNav,
    isMobile,
    vertical
  } = props;
  const accountLevel = useUserSelector('me', 'level');
  const uid = useUserSelector('me', 'id');
  const isPremiumMember = isLevelPremium(accountLevel);
  const ref = useRef<HTMLDivElement>(null);
  const [scrollStart, setScrollStart] = useState(true);
  const [scrollEnd, setScrollEnd] = useState(true);
  const isPremium = isLevelPremium(accountLevel);
  const showDialog = useDialogSelector('show');
  const showDrawer = useDrawerSelector('show');
  const navBelowModal = showDialog || showDrawer;
  const expanded = useLayoutSelector('expandedNav');
  const composeFocused = usePortalStateSelector('composeFocused');
  const quietFocus = usePortalStateSelector('quietFocus');
  const shouldShrinkInset = composeFocused && !quietFocus;
  const leftNav = !vertical;
  const expandedNav = expanded && leftNav;
  const openRoomsCount = useRoomsCount();
  const {
    ageGateActive
  } = useSecureState(SecureStateType.AGE_VERIFICATION);
  const config = useSecureState(SecureStateType.DISPLAY_CONFIG);
  const disableNavDueToAgeGate = config?.settings === GatedDisplayModes.hideEverything;
  const unverified = useAppStateSelector(AppStateType.APP, 'unverified');
  const requiresAccountChange = useAppStateSelector(AppStateType.APP, 'requiresAccountChange');
  const disableNavigation = ageGateActive && disableNavDueToAgeGate || unverified || requiresAccountChange;
  const allMessages = usePMSelector('chats');
  const unreadMessagesCount = usePMSelector('unread');
  const activeMessageID = usePMSelector('activeID');
  const unreadMailCount = useMailStateSelector('unread');
  const broadcasting = useAppStateSelector(AppStateType.MYWEBCAM, 'broadcasting');
  const isMemberBroadcasting = broadcasting === BROADCASTING_STATE.ON;
  const openedRooms = useList(ListType.LAST_VIEWED, {
    returnIDs: false
  });
  const messagesList = Object.keys(allMessages);
  const singleOpenedRoom = openedRooms?.length === 1 ? openedRooms[0]?.slug : '';
  const singlePMInQueue = messagesList.length === 1;
  const archives = 'chat-archives';
  const messages = 'messages';
  const message = 'message';
  const friends = 'friends';
  const search = 'search';
  const modelBrowser = 'model-browser';
  const notes = 'notes';
  const modelsYouMayLike = 'recommendations';
  const rooms = 'rooms';
  const room = 'room';
  const mail = 'mail';
  const news = 'news';
  const home = '';
  const missMFC = 'miss-mfc';
  const privateGallery = 'private-gallery';
  const route = useRouteSelector('visiblePage');
  const routeSlug = useRouteSelector('slug');
  const shareFeedModelName = routeSlug || (slugToID[route] ? route : '');
  const inRoomOrMessage = route === room || route === message;
  const isArchives = route === archives;
  const isMessages = route === messages || route === message;
  const isModelsYouMayLike = route === modelsYouMayLike;
  const isRooms = route === rooms || route === room;
  const isFriends = route === friends;
  const isSearch = route === search;
  const isModelBrowser = route === modelBrowser;
  const isMissMFC = route === missMFC;
  const isNotes = route === notes;
  const isMail = route === mail;
  const isNews = route === news;
  const isHome = route === home;
  const isPrivateGallery = route === privateGallery;

  // if we're in a room then we obviously have at least 1 room open
  const safeOpenRoomCount = route === room ? Math.max(openRoomsCount, 1) : openRoomsCount;
  // const { windowHeight } = useScreenState()
  const windowHeight = useScreenStateSelector('windowHeight');
  const windowWidth = useScreenStateSelector('windowWidth');
  const semiWideFooter = !leftNav && windowWidth >= 500;
  const wideFooter = !leftNav && windowWidth >= APP_BREAKPOINTS.sm;
  const extraWideFooter = !leftNav && windowWidth >= APP_BREAKPOINTS.md;
  const [showMessagesNotification, setShowMessagesNotification] = useState(false);

  // *** must set in useEffect to avoid rehydration issues ***
  // (until we can render the correct notification icon on the server)
  useEffect(() => {
    setShowMessagesNotification(unreadMessagesCount > 0);
  }, [unreadMessagesCount]);
  const checkScrollStart = useCallback((scrollTop?: number) => {
    if (typeof scrollTop === 'undefined' && !ref.current) return;
    const scrollTopValue = scrollTop ?? ref.current?.scrollTop ?? 0;
    const isScrollStart = scrollTopValue === 0;
    setScrollStart(isScrollStart);
  }, []);
  const checkScrollEnd = useCallback((scrollTop?: number, scrollHeight?: number, offsetHeight?: number) => {
    if (typeof scrollTop === 'undefined' && !ref.current) return;
    const scrollTopValue = scrollTop ?? ref.current?.scrollTop ?? 0;
    const scrollHeightValue = scrollHeight ?? ref.current?.scrollHeight ?? 0;
    const offsetHeightValue = offsetHeight ?? ref.current?.offsetHeight ?? 0;
    const isScrollEnd = scrollTopValue >= scrollHeightValue - offsetHeightValue;
    setScrollEnd(isScrollEnd);
  }, []);
  const scrollCheck = useCallback(() => {
    checkScrollStart();
    checkScrollEnd();
  }, [checkScrollStart, checkScrollEnd]);
  const slide = useCallback((shift: number) => {
    if (!ref.current) return;
    // read layout properties before scroll
    const {
      scrollTop,
      scrollHeight,
      offsetHeight
    } = ref.current;
    ref.current.scrollBy({
      top: shift,
      behavior: 'smooth'
    });
    // pass in computed values to avoid reflow (https://gist.github.com/paulirish/5d52fb081b3570c81e3a)
    const computedScrollTop = Math.max(0, scrollTop + shift);
    checkScrollStart(computedScrollTop);
    checkScrollEnd(computedScrollTop, scrollHeight, offsetHeight);
  }, [checkScrollStart, checkScrollEnd]);
  const onClickUp = useCallback(() => {
    slide(-200);
  }, [slide]);
  const onClickDown = useCallback(() => {
    slide(200);
  }, [slide]);
  useEffect(() => {
    scrollCheck();
  }, [windowHeight, scrollCheck]);
  const onClickModelsYouMayLike = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    goToModelsYouMayLike('');
  }, []);
  const onClickRooms = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    if (!isRooms && singleOpenedRoom) {
      openRoom(singleOpenedRoom);
    } else {
      goToRooms();
    }
  }, [isRooms, singleOpenedRoom]);
  const onClickMessages = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    if (!isMessages && singlePMInQueue && activeMessageID) {
      openMessage(activeMessageID, TransitionTypes.NONE);
    } else {
      goToMessages();
    }
  }, [isMessages, singlePMInQueue, activeMessageID]);
  const buttonWrapperStyles = "atm_mk_h2mmj6 atm_vy_1osqo2v";
  const EphemeralChatButtons = <EphemeralChats expandedNav={expandedNav} leftNav={leftNav} messages={allMessages} />;
  const MyWebcamButton = <StyledNavButtonContainer leftNav={leftNav}>
            <NavButton label="myWebcam" expanded={expandedNav} leftNav={leftNav} tooltip={locales.nav.tooltips.myWebcam} disabled={disableNavigation}>
                <ButtonUserBroadcast semiCompact={isMobile} disableTooltip uid={uid} />
            </NavButton>
        </StyledNavButtonContainer>;
  const HomeButton = <StyledNavButtonContainer leftNav={leftNav}>
            <Link nav page="home" disabled={disableNavigation} className={linkStyles}>
                <NavButton label="home" Icon={Home} active={isHome} expanded={expandedNav} leftNav={leftNav} tooltip={locales.nav.tooltips.home} disabled={disableNavigation} styles={buttonWrapperStyles} />
            </Link>
        </StyledNavButtonContainer>;
  const MyAccountButton = <StyledNavButtonContainer leftNav={leftNav}>
            <NavButton label="myAccount" Icon={AccountCircleOutlined} onClick={onMyAccount} expanded={expandedNav} leftNav={leftNav} tooltip={locales.nav.tooltips.myAccount} disabled={disableNavigation} />
        </StyledNavButtonContainer>;
  const LoungeButton = <StyledNavButtonContainer leftNav={leftNav}>
            <NavButton label="lounge" Icon={GlassCocktail} onClick={openLoungesDrawer} expanded={expandedNav} leftNav={leftNav} tooltip={locales.nav.tooltips.lounge} disabled={disableNavigation} />
        </StyledNavButtonContainer>;
  const ExploreButton = <StyledNavButtonContainer leftNav={leftNav}>
            <Link page={search} disabled={disableNavigation} className={linkStyles}>
                <NavButton label="explore" Icon={Explore} active={isSearch} expanded={expandedNav} leftNav={leftNav} tooltip={locales.nav.tooltips.explore} disabled={disableNavigation} />
            </Link>
        </StyledNavButtonContainer>;
  const ModelBrowserButton = <StyledNavButtonContainer leftNav={leftNav}>
            <Link className={linkStyles} nav page={modelBrowser} disabled={disableNavigation}>
                <NavButton label="modelBrowser" Icon={Binoculars} active={isModelBrowser} expanded={expandedNav} leftNav={leftNav} tooltip={locales.nav.tooltips.modelBrowser} disabled={disableNavigation} />
            </Link>
        </StyledNavButtonContainer>;
  const FriendsButton = <StyledNavButtonContainer leftNav={leftNav}>
            <Link nav page={friends} disabled={disableNavigation} className={linkStyles}>
                <NavButton label="friends" Icon={Friend} active={isFriends} expanded={expandedNav} leftNav={leftNav} tooltip={'View and manage your friends.'} disabled={disableNavigation} />
            </Link>
        </StyledNavButtonContainer>;
  const RoomsButton = <StyledNavButtonContainer leftNav={leftNav}>
            <Link nav page={rooms} disabled={disableNavigation} className={linkStyles}>
                <NavButton label="rooms" Icon={Room} count={safeOpenRoomCount} onClick={onClickRooms} active={isRooms} expanded={expandedNav} leftNav={leftNav} tooltip={locales.nav.tooltips.rooms} disabled={disableNavigation} />
            </Link>
        </StyledNavButtonContainer>;
  const MessagesButton = <StyledNavButtonContainer leftNav={leftNav}>
            <Link nav page={messages} disabled={disableNavigation} className={linkStyles}>
                <NavButton label="messages" Icon={Message} count={showMessagesNotification ? unreadMessagesCount : 0} {...singlePMInQueue ? {
        onClick: onClickMessages
      } : {}} active={isMessages} expanded={expandedNav} leftNav={leftNav} tooltip={locales.nav.tooltips.messages} disabled={disableNavigation} />
            </Link>
        </StyledNavButtonContainer>;
  const MailButton = <StyledNavButtonContainer leftNav={leftNav}>
            <Link nav page={mail} disabled={disableNavigation} className={linkStyles}>
                <NavButton label="mail" Icon={Mail} count={unreadMailCount} active={isMail} expanded={expandedNav} leftNav={leftNav} tooltip={locales.nav.tooltips.mail} disabled={disableNavigation} />
            </Link>
        </StyledNavButtonContainer>;
  const MoreButton = <StyledNavButtonContainer leftNav={leftNav}>
            <NavButton label="more" Icon={More} onClick={openAccountDrawer} expanded={expandedNav} leftNav={leftNav} tooltip={locales.nav.tooltips.more} disabled={disableNavigation} />
        </StyledNavButtonContainer>;
  const NewsButton = <StyledNavButtonContainer leftNav={leftNav}>
            <Link nav page={news} disabled={disableNavigation} className={linkStyles}>
                <NavButton label="news" Icon={Newspaper} active={isNews} expanded={expandedNav} leftNav={leftNav} tooltip={locales.nav.tooltips.news} disabled={disableNavigation} />
            </Link>
        </StyledNavButtonContainer>;
  const MissMFCButton = <StyledNavButtonContainer leftNav={leftNav}>
            <Link nav page={missMFC} disabled={disableNavigation} className={linkStyles}>
                <NavButton label="missMFC" Icon={CrownOutline} active={isMissMFC} expanded={expandedNav} leftNav={leftNav} tooltip={locales.nav.tooltips.missMFC} disabled={disableNavigation} />
            </Link>
        </StyledNavButtonContainer>;
  const ShareFeedButton = <StyledNavButtonContainer leftNav={leftNav}>
            <Link nav href={getShareFeedUrl('app_nav_bar', shareFeedModelName)} target={isMobile ? '_self' : '_blank'} className={linkStyles}>
                <NavButton label="shareFeed" Icon={MfcShareFeed} expanded={expandedNav} leftNav={leftNav} tooltip={locales.nav.tooltips.shareFeed} disabled={disableNavigation} />
            </Link>
        </StyledNavButtonContainer>;
  const ModelsYouMayLikeButton = <StyledNavButtonContainer leftNav={leftNav}>
            <Link nav page={modelsYouMayLike} disabled={disableNavigation} className={linkStyles}>
                <NavButton label="recommendations" Icon={Recommendation} active={isModelsYouMayLike} expanded={expandedNav} leftNav={leftNav} onClick={onClickModelsYouMayLike} tooltip={locales.nav.tooltips.recommendations} disabled={disableNavigation} />
            </Link>
        </StyledNavButtonContainer>;
  const ArchivesButton = <StyledNavButtonContainer leftNav={leftNav}>
            <Link nav page={archives} disabled={disableNavigation} className={linkStyles}>
                <NavButton label="archives" Icon={Archives} active={isArchives} expanded={expandedNav} leftNav={leftNav} tooltip={locales.nav.tooltips.archives} disabled={disableNavigation} />
            </Link>
        </StyledNavButtonContainer>;
  const NotesButton = <StyledNavButtonContainer leftNav={leftNav}>
            <Link nav page={notes} disabled={disableNavigation} className={linkStyles}>
                <NavButton label="notes" Icon={Note} active={isNotes} expanded={expandedNav} leftNav={leftNav} tooltip={locales.nav.tooltips.notes} disabled={disableNavigation} />
            </Link>
        </StyledNavButtonContainer>;
  const SupportButton = <StyledNavButtonContainer leftNav={leftNav}>
            <NavButton label="support" Icon={ContactSupport} onClick={openContactSupportDrawer} expanded={expandedNav} leftNav={leftNav} tooltip={locales.nav.tooltips.support} disabled={disableNavigation} />
        </StyledNavButtonContainer>;
  const FeedbackButton = <StyledNavButtonContainer leftNav={leftNav}>
            <NavButton label="feedback" Icon={FeedbackOutlinedIcon} onClick={openUserFeedbackDrawer} expanded={expandedNav} leftNav={leftNav} tooltip={locales.nav.tooltips.feedback} disabled={disableNavigation} />
        </StyledNavButtonContainer>;
  const LogInButton = <StyledNavButtonContainer leftNav={leftNav}>
            <NavButton label="logIn" Icon={LoginIcon} onClick={openAuthenticationDrawer} expanded={expandedNav} leftNav={leftNav} tooltip={locales.nav.tooltips.logIn} />
        </StyledNavButtonContainer>;
  const LogOutButton = <StyledNavButtonContainer leftNav={leftNav}>
            <NavButton label="logOut" Icon={LogoutIcon} onClick={logout} expanded={expandedNav} leftNav={leftNav} tooltip={locales.nav.tooltips.logOut} />
        </StyledNavButtonContainer>;
  const SignUpButton = <StyledNavButtonContainer leftNav={leftNav}>
            <NavButton label="signUp" Icon={AccountCircleOutlined} onClick={openSignupDrawer} expanded={expandedNav} leftNav={leftNav} tooltip={locales.nav.tooltips.signUp} />
        </StyledNavButtonContainer>;
  const AboutButton = <StyledNavButtonContainer leftNav={leftNav}>
            <NavButton label="about" Icon={InfoOutlinedIcon} onClick={openAboutDrawer} expanded={expandedNav} leftNav={leftNav} tooltip={locales.nav.tooltips.about} disabled={disableNavigation} />
        </StyledNavButtonContainer>;
  const PrivateGalleryButton = <StyledNavButtonContainer leftNav={leftNav}>
            <Link nav page={privateGallery} disabled={disableNavigation} className={linkStyles}>
                <NavButton label="privateGallery" Icon={PrivateGallery} active={isPrivateGallery} expanded={expandedNav} leftNav={leftNav} tooltip={locales.nav.tooltips.privateGallery} disabled={disableNavigation} />
            </Link>
        </StyledNavButtonContainer>;

  // console.log('[Nav]', { isMobile, leftNav, inRoomOrMessage, expandedNav })

  return <div id="nav" className={cx(disableNavDueToAgeGate ? 'mfc-image-asset' : '', navRootStyles, showBottomNav ? bottonNavRootStyles : null, showBottomNav && !navBelowModal ? footerPaperHeightAdjustment : null)}>
            {leftNav && !scrollStart && <UpButton onClick={onClickUp} />}
            <div ref={ref} data-testid="Nav__Container" onScroll={scrollCheck} className={cx(shouldShrinkInset ? 'shrink-inset no-scrollbars' : 'no-scrollbars', navContainerStyles, leftNav ? navLeftStyles : navBottomStyles)} style={leftNav ? {
      justifyContent: !isMobile ? 'flex-start' : 'space-between',
      width: !expanded ? COMPONENT_WIDTHS.leftNav : COMPONENT_WIDTHS.leftNavExpanded,
      minWidth: !expanded ? COMPONENT_WIDTHS.leftNav : COMPONENT_WIDTHS.leftNavExpanded,
      maxWidth: !expanded ? COMPONENT_WIDTHS.leftNav : COMPONENT_WIDTHS.leftNavExpanded
    } : {
      height: `calc(var(--saib) + ${COMPONENT_HEIGHTS.navBottom}px)`,
      minHeight: `calc(var(--saib) + ${COMPONENT_HEIGHTS.navBottom}px)`,
      maxHeight: `calc(var(--saib) + ${COMPONENT_HEIGHTS.navBottom}px)`,
      zIndex: !navBelowModal ? PAPER_HEIGHTS.footer : undefined,
      borderTop: !inRoomOrMessage ? `1px solid ${palette.drawerDivider}` : `1px solid ${palette.navBackground}`
    }}>
                {leftNav && isMemberBroadcasting && !ageGateActive && !requiresAccountChange && !unverified && authenticated && isPremium && MyWebcamButton}
                {HomeButton}
                {isPremium && (!isMobile || wideFooter) && LoungeButton}
                {ExploreButton}
                {authenticated && FriendsButton}
                {RoomsButton}
                {MessagesButton}
                {semiWideFooter && MissMFCButton}
                {wideFooter && ShareFeedButton}
                {extraWideFooter && ModelBrowserButton}
                {extraWideFooter && authenticated && ModelsYouMayLikeButton}
                {extraWideFooter && authenticated && ArchivesButton}
                {extraWideFooter && authenticated && NotesButton}
                {extraWideFooter && isPremiumMember && PrivateGalleryButton}
                {!leftNav && MoreButton}
                {leftNav && authenticated && <>
                        {EphemeralChatButtons}
                        {MailButton}
                        {NewsButton}
                        {ArchivesButton}
                        {NotesButton}
                        {isPremiumMember && PrivateGalleryButton}
                        {MissMFCButton}
                        {ModelsYouMayLikeButton}
                        {ModelBrowserButton}
                        {ShareFeedButton}
                        {expandedNav && <>
                                {SupportButton}
                                {FeedbackButton}
                                {AboutButton}
                            </>}
                        {MyAccountButton}
                        {expandedNav && LogOutButton}
                    </>}
                {leftNav && !authenticated && <>
                        {MissMFCButton}
                        {ModelBrowserButton}
                        {ShareFeedButton}
                        {SignUpButton}
                        {LogInButton}
                    </>}
                {leftNav && !expandedNav && MoreButton}
            </div>
            {leftNav && !scrollEnd && <DownButton onClick={onClickDown} />}
        </div>;
});
export default Nav;